// Paliers de quantité.
//
// Un client demande son prix pour 5, 10, 25 et 50. Chez AGC il fallait
// créer quatre pièces identiques — donc quatre endroits à corriger dès
// qu'un temps changeait.
//
// Le prix de chaque palier est calculé par la MÊME fonction que la
// pièce, en ne changeant que la quantité : la première pièce et le
// contrôle s'amortissent tout seuls.
//
// ── DEUX DÉFAUTS CORRIGÉS LE 11 AOÛT 2026 ──
//
// **On ne pouvait ajouter qu'UN palier.** Le bouton proposait toujours
// le même nombre — la quantité de la pièce × 2. Au deuxième clic il
// existait déjà, et la fonction SORTAIT EN SILENCE : pas de palier, pas
// de message, rien. Un bouton qui ne fait rien et ne le dit pas est
// pire qu'un bouton absent.
//
// **La quantité n'était pas modifiable.** Elle s'affichait comme du
// texte. Il fallait supprimer le palier et en recréer un — sauf que
// recréer redonnait le même nombre, celui qu'on venait de supprimer.
//
// Les deux venaient de la même idée fausse : *le programme choisit la
// quantité*. Il ne la choisit pas, il la propose ; c'est le deviseur
// qui sait que le client demande 5, 10, 25 et 50.

import Table from '../../ui/Table.jsx';
import Button from '../../ui/Button.jsx';
import NumberField from '../../ui/NumberField.jsx';
import { formatEuro } from '../../lib/format.js';

/**
 * La prochaine quantité LIBRE.
 *
 * On part du double de la quantité de référence — la suite naturelle
 * d'un chiffrage — et on avance jusqu'à trouver un nombre qui n'est pas
 * déjà pris. Le bouton propose donc toujours quelque chose, et on peut
 * cliquer quatre fois de suite.
 */
function prochaineLibre(declared, qtyRef) {
  const prises = new Set(declared.map((t) => Number(t.qty)));
  // On DOUBLE le plus grand palier existant, plutôt que d'incrémenter :
  // un client demande 5, 10, 25, 50 — pas 6, 7, 8, 9. Quatre clics
  // donnent donc une échelle utilisable sans rien retaper.
  const plusGrand = declared.length ? Math.max(...prises) : 0;
  let n = Math.max(2, (plusGrand || Number(qtyRef) || 1) * 2);
  while (prises.has(n)) n += 1;
  return n;
}

export function QuantityTiers({ piece, tiers, disabled, onChange }) {
  const declared = piece.quantities ?? [];
  // Les prix calculés, retrouvés par quantité. On affiche la liste
  // DÉCLARÉE et non la calculée : c'est celle que le deviseur modifie,
  // et deux listes affichées l'une pour l'autre finiraient par diverger.
  const prix = new Map((tiers ?? []).map((t) => [Number(t.qty), t]));

  function modifier(index, qty) {
    // Une quantité vide n'est PAS un palier de zéro pièce, et ce n'est
    // pas non plus une suppression : c'est une saisie en cours. On ne
    // propage rien, le champ se remet tout seul sur la valeur courante,
    // et la ligne d'aide en dessous dit pourquoi.
    if (qty == null) return;
    if (declared.some((t, i) => i !== index && Number(t.qty) === qty)) return;
    onChange({
      quantities: declared.map((t, i) => (i === index ? { ...t, qty } : t)),
    });
  }

  const doublon = (index, qty) =>
    declared.some((t, i) => i !== index && Number(t.qty) === Number(qty));

  return (
    <div>
      <div className="row row--between">
        <p className="caps">Paliers de quantité</p>
        {/* `secondary` et non `ghost` : quand la liste est vide, ce bouton
            est la SEULE chose à voir dans ce bloc, tout en bas d'un long
            écran. En fantôme, il se cherche — et un outil qu'on ne
            trouve pas est un outil qui n'existe pas. */}
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled}
          onClick={() =>
            onChange({ quantities: [...declared, { qty: prochaineLibre(declared, piece.qty) }] })
          }
        >
          Ajouter un palier
        </Button>
      </div>

      {declared.length === 0 ? (
        <p className="field__hint">
          Aucun palier. Le prix n’est donné que pour la quantité de référence
          ({piece.qty ?? 1}). <strong>Ajoutez-en un</strong> pour montrer au client ce que
          coûtent 10, 25 ou 50 pièces — c’est là que l’amortissement de la mise en route se voit.
        </p>
      ) : (
        <>
          <Table
            columns={[
              {
                key: 'qty',
                label: 'Quantité',
                numeric: true,
                render: (t, index) => (
                  <NumberField
                    label={`Quantité du palier ${index + 1}`}
                    hideLabel
                    value={t.qty}
                    min={1}
                    disabled={disabled}
                    error={doublon(index, t.qty) ? 'Ce palier existe déjà.' : undefined}
                    onChange={(v) => modifier(index, v)}
                  />
                ),
              },
              {
                key: 'price_unit',
                label: 'Prix unitaire',
                numeric: true,
                render: (t) => formatEuro(prix.get(Number(t.qty))?.price_unit),
              },
              {
                key: 'price_total',
                label: 'Total',
                numeric: true,
                render: (t) => formatEuro(prix.get(Number(t.qty))?.price_total),
              },
              {
                key: 'actions',
                label: '',
                render: (t, index) => (
                  <Button
                    variant="ghost"
                    icon
                    aria-label={`Retirer le palier de ${t.qty}`}
                    disabled={disabled}
                    // Par INDEX et non par quantité : deux lignes
                    // peuvent porter la même valeur le temps d'une
                    // frappe, et supprimer « toutes celles qui valent
                    // 10 » en effacerait deux.
                    onClick={() => onChange({ quantities: declared.filter((_, i) => i !== index) })}
                  >
                    ×
                  </Button>
                ),
              },
            ]}
            rows={declared}
            getRowKey={(t, index) => `palier-${index}`}
            caption="Paliers de quantité"
          />
          <p className="field__hint">
            Modifiez la quantité directement dans la case. Un champ laissé vide n’est pas
            enregistré : la valeur précédente revient.
          </p>
        </>
      )}
    </div>
  );
}

export default QuantityTiers;
